import { useState, useEffect } from 'react';
import { Brain, ChevronDown, ChevronUp, Bell, Landmark, Hammer, Bitcoin, Globe, ArrowRight } from 'lucide-react';
import ModuleContainer from '../components/layout/ModuleContainer.jsx';
import RecommendationCard from '../components/portfolio-command/RecommendationCard.jsx';
import ShadowTracker from '../components/portfolio-command/ShadowTracker.jsx';
import MnemosyneStats from '../components/portfolio-command/MnemosyneStats.jsx';
import CryptoPulsePanel from '../components/CryptoPulsePanel.jsx';
import { usePage } from '../contexts/PageContext.jsx';

export default function PortfolioCommand() {
  const { setPage } = usePage();
  useEffect(() => { setPage('portfolio-command'); }, []);

  const [recs, setRecs] = useState([]);
  const [preds, setPreds] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [calibration, setCalibration] = useState([]);
  const [stats, setStats] = useState(null);
  const [consensus, setConsensus] = useState([]);
  const [smSignals, setSmSignals] = useState([]);
  const [urgentPicks, setUrgentPicks] = useState([]);
  const [predsOpen, setPredsOpen] = useState(false);
  const [lessonsOpen, setLessonsOpen] = useState(false);
  const [calOpen, setCalOpen] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const [recRes, predRes, lesRes, calRes, statRes] = await Promise.all([
          fetch('/api/kairos/recommendations?status=active'),
          fetch('/api/kairos/predictions?status=active'),
          fetch('/api/kairos/lessons?days=7'),
          fetch('/api/kairos/calibration'),
          fetch('/api/kairos/stats'),
        ]);
        if (recRes.ok) { const d = await recRes.json(); setRecs(d.recommendations || []); }
        if (predRes.ok) { const d = await predRes.json(); setPreds(d.predictions || []); }
        if (lesRes.ok) { const d = await lesRes.json(); setLessons(d.lessons || []); }
        if (calRes.ok) { const d = await calRes.json(); setCalibration(d.calibration || []); }
        if (statRes.ok) setStats(await statRes.json());
        try { const cRes = await fetch('/api/voices/consensus'); if (cRes.ok) setConsensus(await cRes.json()); } catch {}
        try { const smRes = await fetch('/api/smart-money/signals'); if (smRes.ok) setSmSignals(await smRes.json()); } catch {}
        try { const upRes = await fetch('/api/watchlist/signals/urgent?limit=3'); if (upRes.ok) setUrgentPicks(await upRes.json()); } catch {}
      } catch { /* silent */ }
    }
    load();
    const interval = setInterval(load, 120000);
    return () => clearInterval(interval);
  }, []);

  function handleAskAbout(rec) {
    // Use global chat opener exposed by KairosGlobalChat
    if (window.__kairosOpenChat) window.__kairosOpenChat(rec);
  }

  const tacticalPreds = preds.filter(p => p.timeframe === 'tactical');
  const swingPreds = preds.filter(p => p.timeframe === 'swing');
  const strategicPreds = preds.filter(p => p.timeframe === 'strategic');

  return (
    <div className="space-y-6">
      {/* Header — Sprint 21.5: added AlertsBell (UI-freeze exception) */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Brain className="w-6 h-6 text-korinth-accent" />
          <h1 className="text-2xl font-bold text-white">Portfolio Command</h1>
          <span className="text-korinth-muted text-sm">KAIROS recommendations & intelligence</span>
        </div>
        <AlertsBell />
      </div>

      {/* KAIROS Summary Bar */}
      {stats && (
        <div className="bg-korinth-surface border border-korinth-border rounded-lg px-4 py-3">
          <div className="flex items-center gap-3 flex-wrap text-xs font-mono">
            <div className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 rounded-full bg-korinth-accent animate-pulse" />
              <span className="text-korinth-accent font-bold">KAIROS ACTIVE</span>
            </div>
            <span className="text-korinth-muted">|</span>
            <span className="text-korinth-text">{stats.totalObservations} observations</span>
            <span className="text-korinth-muted">|</span>
            <span className="text-korinth-text">{stats.activePredictions} active predictions</span>
            <span className="text-korinth-muted">|</span>
            <span className="text-korinth-text">{stats.activeRecommendations} active recommendations</span>
            <span className="text-korinth-muted">|</span>
            <span className="text-korinth-text">Avg confidence: {stats.avgConfidence || '—'}%</span>
          </div>
        </div>
      )}

      {/* Urgent Watchlist Signals */}
      {urgentPicks.length > 0 && (
        <div>
          <h2 className="text-xs font-mono text-korinth-warning uppercase tracking-wider mb-2">Urgent Watchlist Signals</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {urgentPicks.map(u => (
              <a key={u.ticker} href="/watchlist" className={`px-3 py-2 rounded-lg border-l-3 ${u.kairos_signal === 'buy' ? 'border-l-korinth-accent bg-korinth-accent/5' : 'border-l-korinth-danger bg-korinth-danger/5'} hover:opacity-80 transition-opacity`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-mono font-bold text-white">{u.ticker}</span>
                  <span className="text-xs font-mono font-bold" style={{ color: u.kairos_signal === 'buy' ? '#10b981' : '#ef4444' }}>{u.kairos_signal?.toUpperCase()} {u.signal_strength}/100</span>
                </div>
                <div className="text-[9px] text-korinth-muted font-mono">{u.name} | {u.sector_name}</div>
                {u.signal_thesis && <div className="text-[8px] text-korinth-accent mt-0.5">{u.signal_thesis}</div>}
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Sprint 21.7.2: Domain Command Centers — 4 summary cards drilling
          into each specialist home. Each fetches its own summary_strip. */}
      <CommandCenterSummaryCards />

      {/* Active Recommendations */}
      <div>
        <h2 className="text-xs font-mono text-korinth-muted uppercase tracking-wider mb-3">
          Active Recommendations ({recs.length})
        </h2>
        {recs.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {recs.map(r => (
              <RecommendationCard key={r.id} rec={r} onAskAbout={handleAskAbout} consensus={consensus.find(c => c.asset === r.asset)} smartMoney={smSignals.find(s => s.asset === r.asset)} />
            ))}
          </div>
        ) : (
          <div className="bg-korinth-surface border border-korinth-border rounded-lg px-4 py-8 text-center">
            <Brain className="w-8 h-8 text-korinth-muted mx-auto mb-2" />
            <p className="text-xs font-mono text-korinth-muted">KAIROS is analyzing markets. Recommendations will appear here after the first cycle (every 2 hours).</p>
          </div>
        )}
      </div>

      {/* Crypto Pulse (Sprint 18 — ambient glance panel) */}
      <CryptoPulsePanel />

      {/* Shadow Tracker */}
      <ShadowTracker />

      {/* Active Predictions (collapsible) */}
      <ModuleContainer
        title={
          <button onClick={() => setPredsOpen(!predsOpen)} className="flex items-center gap-2 w-full text-left">
            {predsOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            <span>Active Predictions ({preds.length})</span>
          </button>
        }
        subtitle={predsOpen ? `Tactical: ${tacticalPreds.length} | Swing: ${swingPreds.length} | Strategic: ${strategicPreds.length}` : null}
      >
        {predsOpen ? (
          <div className="space-y-1.5 max-h-64 overflow-y-auto">
            {preds.length > 0 ? preds.map((p, i) => (
              <div key={p.id || i} className="flex items-center gap-2 px-3 py-2 bg-korinth-bg rounded text-xs font-mono">
                <span className={p.direction === 'bullish' ? 'text-korinth-accent' : 'text-korinth-danger'}>
                  {p.direction === 'bullish' ? '▲' : '▼'}
                </span>
                <span className="text-white font-bold w-12">{p.asset}</span>
                <span className="text-korinth-muted px-1.5 py-0.5 rounded bg-korinth-border text-[9px]">{p.timeframe}</span>
                <span className="text-korinth-text">{p.confidence}%</span>
                {p.target_price && <span className="text-korinth-muted ml-auto">→ {p.target_price}</span>}
              </div>
            )) : <p className="text-[10px] text-korinth-muted font-mono text-center py-4">No active predictions yet</p>}
          </div>
        ) : (
          <p className="text-[10px] text-korinth-muted font-mono">Click to expand</p>
        )}
      </ModuleContainer>

      {/* Recent Lessons (collapsible) */}
      <ModuleContainer
        title={
          <button onClick={() => setLessonsOpen(!lessonsOpen)} className="flex items-center gap-2 w-full text-left">
            {lessonsOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            <span>Recent Lessons ({lessons.length})</span>
          </button>
        }
      >
        {lessonsOpen ? (
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {lessons.length > 0 ? lessons.map((l, i) => (
              <div key={l.id || i} className="px-3 py-2 bg-korinth-bg rounded border-l-2 border-korinth-accent/30">
                <p className="text-xs text-korinth-text">{l.lesson_text}</p>
                <p className="text-[9px] text-korinth-muted mt-1">{l.source} — {l.created_at?.slice(0, 10)}</p>
              </div>
            )) : <p className="text-[10px] text-korinth-muted font-mono text-center py-4">No lessons yet — reflection runs daily at 03:00 UTC</p>}
          </div>
        ) : (
          <p className="text-[10px] text-korinth-muted font-mono">Click to expand</p>
        )}
      </ModuleContainer>

      {/* Calibration (collapsible) */}
      <ModuleContainer
        title={
          <button onClick={() => setCalOpen(!calOpen)} className="flex items-center gap-2 w-full text-left">
            {calOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            <span>Confidence Calibration</span>
          </button>
        }
      >
        {calOpen ? (
          calibration.length > 0 ? (
            <div className="space-y-2">
              {calibration.map((b, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="text-[10px] font-mono text-korinth-muted w-16">{b.confidence_bucket}% conf</span>
                  <div className="flex-1 h-2 bg-korinth-bg rounded-full overflow-hidden">
                    <div className="h-full bg-korinth-accent rounded-full" style={{ width: `${(b.calibrated_rate || 0) * 100}%` }} />
                  </div>
                  <span className="text-[10px] font-mono text-korinth-text w-20 text-right">
                    {b.correct_count}/{b.predictions_count} ({b.calibrated_rate != null ? (b.calibrated_rate * 100).toFixed(0) + '%' : '—'})
                  </span>
                </div>
              ))}
              <p className="text-[9px] text-korinth-muted font-mono mt-2">
                "When KAIROS says 80% confident, the actual hit rate is shown above. Better calibration = better predictions."
              </p>
            </div>
          ) : <p className="text-[10px] text-korinth-muted font-mono text-center py-4">Not enough resolved predictions yet for calibration data</p>
        ) : (
          <p className="text-[10px] text-korinth-muted font-mono">Click to expand</p>
        )}
      </ModuleContainer>

      {/* Mnemosyne Memory */}
      <MnemosyneStats />

      {/* Global KAIROS chat FAB is rendered in App.jsx layout */}
    </div>
  );
}

/**
 * AlertsBell — Sprint 21.5 UI-freeze exception.
 * Compact unread alerts badge linking to /alerts. Polls every 30s.
 */
function AlertsBell() {
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    async function load() {
      try {
        const lastRead = localStorage.getItem('korinth_alerts_last_read') || new Date(0).toISOString();
        const r = await fetch(`/api/alerts/unread-count?since=${encodeURIComponent(lastRead)}`);
        if (r.ok) { const d = await r.json(); setUnread(d.unread || 0); }
      } catch {}
    }
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, []);

  return (
    <a
      href="/alerts"
      className="relative flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-korinth-border hover:border-korinth-accent/40 bg-korinth-surface text-xs font-mono text-korinth-text transition-colors"
      title={`${unread} unread alerts`}
    >
      <Bell className="w-3.5 h-3.5 text-korinth-accent" />
      <span>Alerts</span>
      {unread > 0 && (
        <span className="flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-korinth-accent text-black text-[9px] font-bold">
          {unread > 99 ? '99+' : unread}
        </span>
      )}
    </a>
  );
}

// Sprint 21.7.2: Command Center summary cards (ADR-027). Each card pulls
// its own /api/command/:domain/overview and shows regime + notable move +
// drill link. Stacks on mobile.
const DOMAIN_CARDS = [
  { domain: 'equity',       title: 'Equity',       Icon: Landmark, subtitle: 'Individual names' },
  { domain: 'fixed-income', title: 'Fixed Income', Icon: Hammer,   subtitle: 'Rates · credit' },
  { domain: 'crypto',       title: 'Crypto',       Icon: Bitcoin,  subtitle: 'BTC · majors · RWA' },
  { domain: 'macro',        title: 'Macro',        Icon: Globe,    subtitle: 'Fed · geopolitics' },
];

function CommandCenterSummaryCards() {
  const [data, setData] = useState({});
  useEffect(() => {
    async function load() {
      const next = {};
      await Promise.all(DOMAIN_CARDS.map(async ({ domain }) => {
        try {
          const r = await fetch(`/api/command/${domain}/overview`);
          if (r.ok) next[domain] = await r.json();
        } catch {}
      }));
      setData(next);
    }
    load();
    const iv = setInterval(load, 120_000);
    return () => clearInterval(iv);
  }, []);

  return (
    <div>
      <h2 className="text-xs font-mono text-korinth-muted uppercase tracking-wider mb-3">Command Centers</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {DOMAIN_CARDS.map(({ domain, title, Icon, subtitle }) => {
          const d = data[domain];
          const regime = d?.summary_strip?.regime || '—';
          const framing = d?.kairos_framing;
          const shortNote = (framing && !framing._stub && framing.headline) || d?.summary_strip?.notable_move || subtitle;
          return (
            <a
              key={domain}
              href={`/command/${domain}`}
              className="group bg-korinth-surface border border-korinth-border rounded-lg p-3 hover:border-korinth-accent/50 transition-colors flex flex-col gap-1.5"
              style={{ minHeight: 104 }}
            >
              <div className="flex items-center gap-2">
                <Icon className="w-4 h-4 text-korinth-accent" />
                <span className="text-xs font-mono font-bold uppercase tracking-wider text-white">{title}</span>
              </div>
              <div className="text-[10px] font-mono text-korinth-muted uppercase tracking-wider">regime · {regime}</div>
              <p className="text-[11px] font-mono text-korinth-text line-clamp-2 flex-1">{shortNote}</p>
              <div className="flex items-center gap-1 text-[10px] font-mono text-korinth-accent group-hover:translate-x-0.5 transition-transform">
                Drill <ArrowRight className="w-3 h-3" />
              </div>
            </a>
          );
        })}
      </div>
    </div>
  );
}
